import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Tooltip from "@mui/joy/Tooltip";

import IconButton from "@mui/joy/IconButton";
import {
  Home,
  LightMode,
  DarkMode,
  Logout,
  Business,
  AdminPanelSettings,
  School as SchoolIcon,
} from "@mui/icons-material";
import {
  Stack,
  Avatar,
  Dropdown,
  MenuButton,
  Menu,
  MenuItem,
  Typography,
  ListDivider,
  ListItemDecorator,
} from "@mui/joy";
import { useColorScheme } from "@mui/joy/styles";
import { useReadUser } from "../services/api";
import { User } from "../common/types";
import { useTutorial } from "./Tutorial";
import { IDA_HISTORY_PILL_KEY } from "../modules/ida/components/FullPageChatLayout";
import { isDevFeaturesEnabled } from "../utils/devFeatures";

function getInitials(name?: string, email?: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2)
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0][0].toUpperCase();
  }
  if (email) return email[0].toUpperCase();
  return "?";
}

const UpperbarMenu: React.FC = () => {
  const { mode, setMode } = useColorScheme();
  const readUserAPI = useReadUser();
  const [user, setUser] = useState<User | null>(null);
  const { resetAllDismissed } = useTutorial();

  // Fetch user data on mount
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await readUserAPI();
        setUser(userData);
      } catch (err) {
        console.error("Failed to fetch user:", err);
      }
    };
    fetchUser();
  }, [readUserAPI]);

  // Check if user is org admin
  const isOrgAdmin = user?.role_in_org === "admin";
  const isMetalMindAdmin = user?.is_metalmind_admin === true;

  // Toggle between dark and light theme
  const toggleTheme = () => {
    setMode(mode === "light" ? "dark" : "light");
  };

  return (
    <Stack direction="row" spacing={1}>
      <Tooltip title="Home">
        <Link to="/">
          <IconButton>
            <Home />
          </IconButton>
        </Link>
      </Tooltip>
      {isOrgAdmin && (
        <Tooltip title="Organization Settings">
          <Link to="/organization/settings">
            <IconButton variant="plain" color="neutral">
              <Business />
            </IconButton>
          </Link>
        </Tooltip>
      )}
      {isDevFeaturesEnabled() && (
        <Tooltip title="Reset tutorial hints">
          <IconButton
            onClick={() => {
              resetAllDismissed();
              try { localStorage.removeItem(IDA_HISTORY_PILL_KEY); } catch {}
            }}
            variant="plain"
            color="neutral"
          >
            <SchoolIcon />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip
        title={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      >
        <IconButton onClick={toggleTheme}>
          {mode === "dark" ? <LightMode /> : <DarkMode />}
        </IconButton>
      </Tooltip>
      <Dropdown>
        <MenuButton
          slots={{ root: IconButton }}
          slotProps={{ root: { variant: "plain" } }}
        >
          <Avatar
            size="sm"
            sx={(theme) => ({
              width: 24,
              height: 24,
              fontSize: "0.75rem",
              ...(theme.palette.mode === "light" && {
                bgcolor: "neutral.500",
                color: "neutral.200",
              }),
            })}
          >
            {getInitials(user?.name, user?.email)}
          </Avatar>
        </MenuButton>
        <Menu placement="bottom-end" size="sm">
          <MenuItem disabled sx={{ opacity: 1, fontStyle: "normal" }}>
            <Typography level="body-xs">{user?.email}</Typography>
          </MenuItem>
          <ListDivider />
          {isMetalMindAdmin && (
            <>
              <MenuItem component={Link} to="/admin/metalmind">
                <ListItemDecorator>
                  <AdminPanelSettings />
                </ListItemDecorator>
                MetalMind Admin
              </MenuItem>
              <ListDivider />
            </>
          )}
          <MenuItem component={Link} to="/logout">
            <ListItemDecorator>
              <Logout />
            </ListItemDecorator>
            Log out
          </MenuItem>
        </Menu>
      </Dropdown>
    </Stack>
  );
};

export default UpperbarMenu;
